import React, { CSSProperties, useEffect, useRef, useState } from "react";
import "./QrReader.css";
import QrScanner from "qr-scanner";
import QrFrame from "./assets/qr-frame.svg?url";
import getSortedCameras from "./cameraSelector.ts";
import { CircularProgress } from "@mui/material";
import scan from "./pages/Scan.tsx";

interface QrReaderProps {
    onScanResult(result: string, n:number): Promise<void>;
}

const QrReader: React.FC<QrReaderProps> = (props) => {
    const scanner = useRef<QrScanner | null>(null);
    const videoEl = useRef<HTMLVideoElement | null>(null);
    const qrBoxEl = useRef<HTMLDivElement | null>(null);
    const [qrOn, setQrOn] = useState<boolean>(true);
    const [showWaiting, setShowWaiting] = useState<boolean>(false);
    const lastScannedRef = useRef<string | null>(null); // для дедупликации

    const onScanSuccess = (result: QrScanner.ScanResult) => {
        // Защита от повторного сканирования одного и того же QR-кода
        if (lastScannedRef.current === result.data) {
            return;
        }

        lastScannedRef.current = result.data;
        scanner.current?.pause();
        setShowWaiting(true);

        props.onScanResult(result.data, 1).finally(() => {
            setShowWaiting(false);
            // Разрешаем сканировать тот же QR снова через 1.5 секунды
            setTimeout(() => {
                lastScannedRef.current = null;
            }, 1500);
            scanner.current?.start();
        });
    };

    const onScanFail = (err: string | Error) => {
        // Можно логировать ошибку, если нужно
        console.warn("QR scan failed:", err);
    };

    useEffect(() => {
        let isMounted = true;

        getSortedCameras().then(cameras => {
            if (!isMounted || cameras.length === 0 || !videoEl.current) return;

            // Создаём сканер только один раз
            if (!scanner.current) {
                scanner.current = new QrScanner(
                    videoEl.current,
                    onScanSuccess,
                    {
                        onDecodeError: onScanFail,
                        preferredCamera: cameras[0].deviceId,
                        highlightScanRegion: true,
                        highlightCodeOutline: true,
                        overlay: qrBoxEl.current || undefined,
                        maxScansPerSecond: 1,
                    }
                );
            }

            scanner.current
                ?.start()
                .then(() => {
                    if (isMounted) setQrOn(true);
                })
                .catch((err) => {
                    console.error("Failed to start camera:", err);
                    if (isMounted) setQrOn(false);
                });
        });

        return () => {
            isMounted = false;
            if (scanner.current) {
                scanner.current?.stop();
                scanner.current = null;
            }
        };
    }, []);

    // Предупреждение о доступе к камере
    useEffect(() => {
        if (!qrOn) {
            alert(
                "Camera is blocked or not accessible. Please allow camera in your browser permissions and reload the page."
            );
        }
    }, [qrOn]);

    const overlayStyle: CSSProperties = {
        position: "absolute",
        zIndex: 9999,
        background: "rgba(0,0,0,0.5)",
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    };

    return (

        <div className="qr-reader">
            {showWaiting && (
                <div style={overlayStyle}>
                    <CircularProgress
                        size="70px"
                        style={{color: "whitesmoke"}}
                    />
                </div>
            )}
            <video ref={videoEl}/>
                    <div ref={qrBoxEl} className="qr-box">
                        <img
                            src={QrFrame}
                            alt="QR Frame"
                            width={156}
                            height={156}
                            className="qr-frame"
                        />
                    </div>
        </div>
    );
};

export default QrReader;